import { useLazyQuery } from "@apollo/client";
import Sort from "@components/common/inbox/Sort";
import { cache } from "@components/index/providers";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GET_INBOX_NOTES from "@graphql/queries/GetInboxNotes";
import GET_INBOX_TAB_COUNT from "@graphql/queries/GetInboxTabCount";
import { evictNoteCache } from "@src/utils";
import mixpanel from "mixpanel-browser";
import { useEffect, useState, useCallback } from "react";

import InboxWindow from "./InboxWindow";
import NoteFilters from "./NoteFilters";
import { TAB_ARRAY } from "../../constants";
import {
  useLocalStorage,
  useNoteMultiselectContext,
  useRecordingJobType,
  useWatchUserWsEvent,
} from "../../hooks";
import { useTeam } from "../../hooks/use-team";
import { MultiSelectActionContainer } from "../common/multiselect/MultiSelectActionContainer";
import Tabs from "../common/tabs/Tabs";
import PageTitle from "../layout/PageTitle";

export default function Inbox() {
  // States and constants
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem("lastActiveTab");
    return savedTab !== null ? savedTab : "savedDrafts";
  });
  const activeTabObject = TAB_ARRAY.find(
    (tab) => tab.id === activeTab,
  );
  const {
    currentTeam,
    selectedTeamMembers,
    setSelectedTeamMembers,
    refreshCurrentTeam,
  } = useTeam();
  const { filterJobType, setFilterJobType } = useRecordingJobType();

  const [selectedRange, _] = useLocalStorage("selected-date-range", {
    startDate: null,
    endDate: null,
    key: "selection",
  });
  const [notesData, setNotesData] = useState(null);
  const [tabCountData, setTabCountData] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [sortAscending, setSortAscending] = useState(false);
  const [queryOptions, setQueryOptions] = useState({
    filter: activeTabObject?.serverFilterKey,
    limit: 15,
    offset: 0,
    startDate: selectedRange.startDate,
    endDate: selectedRange.endDate,
    teamUuid: currentTeam?.uuid,
    userUuids: selectedTeamMembers,
    sortAscending: false,
    jobType: filterJobType,
  });

  // Hooks
  const { resetSelectedNotesList } = useNoteMultiselectContext();

  useEffect(() => {
    setQueryOptions((prevOptions) => {
      const updatedOptions = {
        ...prevOptions,
        teamUuid: currentTeam?.uuid,
        userUuids: selectedTeamMembers,
      };
      return updatedOptions;
    });
  }, [currentTeam, selectedTeamMembers]);

  const [tabCountRefetch] = useLazyQuery(GET_INBOX_TAB_COUNT, {
    variables: queryOptions,
    onCompleted: (data) => {
      setTabCountData(data);
    },
  });

  const [notesRefetch, { fetchMore }] = useLazyQuery(
    GET_INBOX_NOTES,
    {
      variables: queryOptions,
      onError: () => {
        setNotesLoading(false);
      },
      onCompleted: (data) => {
        setNotesData(data);
        setNotesLoading(false);
      },
    },
  );

  useWatchUserWsEvent({
    onMessage: () => {
      setShouldRefetch(true);
    },
    onConnect: () => {
      setShouldRefetch(true);
    },
    eventName: "note_update",
  });

  // Data fetching functions
  const fetchNotes = useCallback(() => {
    notesRefetch({ variables: queryOptions });
    tabCountRefetch({ variables: queryOptions });
  }, [queryOptions, notesRefetch, tabCountRefetch]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    if (shouldRefetch) {
      evictNoteCache(cache);
      fetchNotes();
      setShouldRefetch(false);
    }
  }, [shouldRefetch]);

  const handleFetchMore = async () => {
    const { data: newData } = await fetchMore({
      variables: {
        ...queryOptions,
        offset: notesData.inboxNotes.notes.length,
      },
    });
    setNotesData((prevData) => ({
      inboxNotes: {
        ...prevData.inboxNotes,
        notes: [
          ...prevData.inboxNotes.notes,
          ...newData.inboxNotes.notes,
        ],
        totalNotes: newData.inboxNotes.totalNotes,
      },
    }));
    setNotesLoading(false);
  };

  useEffect(() => {
    // "document.documentElement.scrollTo" is the magic for React Router Dom v6
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // Optional if you want to skip the scrolling animation
    });
  }, []);

  useEffect(() => {
    // show loading only on tab change and first load
    // otherwise, other note actions will show loading
    // and the UI will feel jumpy
    setNotesLoading(true);
  }, []);

  useEffect(() => {
    setQueryOptions((prevOptions) => ({
      ...prevOptions,
      filter: activeTabObject?.serverFilterKey,
    }));
    localStorage.setItem("lastActiveTab", activeTab); // Update local storage when the activeTab changes, record the last active tab
  }, [activeTab]);

  useEffect(() => {
    setQueryOptions((prevOptions) => ({
      ...prevOptions,
      jobType: filterJobType?.value,
    }));
  }, [filterJobType]);

  const handleSort = async () => {
    const newSortAscending = !sortAscending;
    setSortAscending(newSortAscending);

    setQueryOptions((prev) => ({
      ...prev,
      sortAscending: newSortAscending,
    }));
    setShouldRefetch(true);
  };

  return (
    <>
      <PageTitle title="Inbox">
        <div className="flex flex-row items-center justify-between space-x-6 mt-2 pt-2">
          <div className="flex flex-row items-center space-x-1">
            <FontAwesomeIcon
              icon={faFilter}
              className="text-gray-500"
            />
            <NoteFilters
              queryOptions={queryOptions}
              setQueryOptions={setQueryOptions}
              currentTeam={currentTeam}
              selectedTeamMembers={selectedTeamMembers}
              setSelectedTeamMembers={setSelectedTeamMembers}
              selectedJobType={filterJobType}
              setSelectedJobType={setFilterJobType}
              refreshCurrentTeam={refreshCurrentTeam}
            />
          </div>
          <div className="flex flex-row items-center space-x-1">
            <Sort sortAscending={sortAscending} onSort={handleSort} />
          </div>
        </div>
      </PageTitle>

      <div className="mt-6">
        <Tabs
          id="inboxTabs"
          tabArray={TAB_ARRAY}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabCounts={tabCountData?.inboxTabCount}
          handleTabClick={(tabId) => {
            if (tabId !== activeTab) {
              mixpanel.track("Switched Inbox Tab", {
                oldTab: activeTab,
                newTab: tabId,
              });
              resetSelectedNotesList();
              setNotesLoading(true);
              setActiveTab(tabId);
            }
          }}
        >
          {TAB_ARRAY.map((tab) => {
            if (tab.id !== activeTab) {
              return null;
            }

            return (
              <div key={tab.id} id={tab.id}>
                <InboxWindow
                  tabId={tab.id}
                  tabTitle={tab.title}
                  colorStyles={tab.colorStyles}
                  description={[tab.description]}
                  activeTab={activeTab}
                  isExportWindow={tab?.isExportWindow}
                  isAlreadyExported={!!tab?.isAlreadyExported}
                  data={notesData}
                  refetch={() => {
                    setShouldRefetch(true);
                  }}
                  fetchMore={handleFetchMore}
                  notesLoading={notesLoading}
                />
              </div>
            );
          })}
        </Tabs>
      </div>
      <MultiSelectActionContainer
        notes={notesData?.inboxNotes?.notes}
        refetch={fetchNotes}
      />
    </>
  );
}
